import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type LicenseKey } from '../../api/admin';
import { confirm } from '../../components/ConfirmDialog';

const STATUS_CLS: Record<string, string> = { active: 'badge-paid', revoked: 'badge-overdue', expired: 'badge-gray' };

export default function AdminLicenseKeysPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState<{ clientId: string; expiresAt: string; notes: string }>({ clientId: '', expiresAt: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [masterVisible, setMasterVisible] = useState(false);

  const { data: keys = [], isLoading } = useQuery({ queryKey: ['admin-license-keys'], queryFn: adminApi.listKeys, staleTime: 30 * 1000 });
  const { data: clients = [] } = useQuery({ queryKey: ['admin-clients', ''], queryFn: () => adminApi.listClients(), staleTime: 30 * 1000 });
  const { data: masterData } = useQuery({ queryKey: ['admin-master-key'], queryFn: adminApi.getMasterKey, staleTime: 30 * 1000 });
  const del = useMutation({ mutationFn: adminApi.deleteKey, onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-license-keys'] }) });
  const revoke = useMutation({
    mutationFn: (id: string) => adminApi.updateKey(id, { status: 'revoked' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-license-keys'] }),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => adminApi.updateKey(id, { status: 'active' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-license-keys'] }),
  });

  function copyKey(k: string) {
    navigator.clipboard.writeText(k);
    setCopied(k);
    setTimeout(() => setCopied(null), 2000);
  }

  async function handleGenerate() {
    setError(null); setSaving(true);
    try {
      await adminApi.createKey({ clientId: form.clientId || undefined, expiresAt: form.expiresAt || undefined, notes: form.notes || undefined });
      qc.invalidateQueries({ queryKey: ['admin-license-keys'] });
      setModal(false);
      setForm({ clientId: '', expiresAt: '', notes: '' });
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed'); }
    finally { setSaving(false); }
  }

  const masterKey = masterData?.key ?? '';

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">License Keys</h1>
        <button className="btn btn-primary" onClick={() => setModal(true)}>+ Generate Key</button>
      </div>

      {/* Master key banner */}
      <div className="card" style={{ marginBottom: '1.25rem', padding: '1rem 1.25rem' }}>
        <div style={{ fontWeight: 700, marginBottom: '0.5rem', fontSize: '0.875rem' }}>🔑 Master Premium Key (all clients)</div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <code style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.35rem 0.75rem', letterSpacing: '0.1em', fontWeight: 700 }}>
            {masterVisible ? masterKey : '●●●●-●●●●-●●●●-●●●●-●●●●'}
          </code>
          <button className="btn btn-ghost btn-sm" onClick={() => setMasterVisible(v => !v)}>{masterVisible ? 'Hide' : 'Show'}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => copyKey(masterKey)}>{copied === masterKey ? '✓ Copied!' : 'Copy'}</button>
        </div>
      </div>

      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : keys.length === 0
          ? <div className="empty-state"><div className="empty-state-icon">🔑</div><div className="empty-state-text">No per-client keys generated yet</div></div>
          : (
            <table>
              <thead><tr><th>Key</th><th>Client</th><th>Status</th><th>Expires</th><th>Notes</th><th>Created</th><th style={{ width: 140 }}>Actions</th></tr></thead>
              <tbody>
                {keys.map((k: LicenseKey) => (
                  <tr key={k.id}>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <code style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.05em' }}>{k.keyValue}</code>
                        <button className="btn btn-ghost btn-sm" style={{ padding: '0.15rem 0.4rem', fontSize: '0.72rem' }} onClick={() => copyKey(k.keyValue)}>
                          {copied === k.keyValue ? '✓' : '📋'}
                        </button>
                      </div>
                    </td>
                    <td style={{ fontSize: '0.83rem' }}>{k.client?.business ?? <span style={{ color: 'var(--text-muted)' }}>Unassigned</span>}</td>
                    <td><span className={`badge ${STATUS_CLS[k.status] ?? 'badge-gray'}`}>{k.status}</span></td>
                    <td style={{ fontSize: '0.83rem', color: k.expiresAt && k.expiresAt < new Date().toISOString() ? 'var(--danger)' : 'var(--text-2)' }}>
                      {k.expiresAt ? k.expiresAt.slice(0, 10) : '—'}
                    </td>
                    <td style={{ fontSize: '0.83rem', color: 'var(--text-muted)' }}>{k.notes ?? '—'}</td>
                    <td style={{ fontSize: '0.83rem', color: 'var(--text-muted)' }}>{k.createdAt.slice(0, 10)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        {k.status === 'active' && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={async () => { if (await confirm({ message: 'Revoke this key? The client will lose access.', danger: true })) revoke.mutate(k.id); }}>Revoke</button>}
                        {k.status === 'revoked' && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--success, #16a34a)' }} onClick={() => reactivate.mutate(k.id)}>Re-activate</button>}
                        <button className="btn btn-danger btn-sm" onClick={async () => { if (await confirm({ message: 'Permanently delete this key?', danger: true })) del.mutate(k.id); }}>Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <span className="modal-title">Generate License Key</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-box">{error}</div>}
              <div className="form-row">
                <label>Assign to Client (optional)
                  <select value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}>
                    <option value="">— Unassigned —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.business} ({c.name})</option>)}
                  </select>
                </label>
              </div>
              <div className="form-row">
                <label>Expiry Date (optional)
                  <input type="date" value={form.expiresAt} onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
                </label>
              </div>
              <div className="form-row">
                <label>Notes
                  <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. 1-year premium key" />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleGenerate} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '🔑 Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
